import { Package, Pencil, Plus, RotateCw, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PRODUCT_TYPES,
  PRODUCT_TYPE_LABELS_PLURIEL,
  SEUIL_STOCK_FAIBLE,
  type Product,
  type ProductType,
} from '@sirajoptic/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Checkbox, Input } from '@/components/ui/field';
import { ChargementBloc, EtatErreur, EtatVide } from '@/components/ui/feedback';
import { Pagination } from '@/components/ui/pagination';
import { Tabs } from '@/components/ui/tabs';
import { TBody, TD, TH, THTri, THead, TR, Table } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { PageBody, PageHeader } from '@/layouts/AppShell';
import { date, dh, entier, tronquer } from '@/lib/format';
import { useProduits, useSupprimerProduit } from './api/stock.api';
import { ProduitDialog } from './components/ProduitDialog';
import { RestockDialog } from './components/RestockDialog';

type Tri = 'nom' | 'quantite' | 'prix_vente' | 'date_ajout';
type Filtre = ProductType | 'TOUS';

const ONGLETS = [
  { valeur: 'TOUS' as const, libelle: 'Tous' },
  ...PRODUCT_TYPES.map((t) => ({ valeur: t, libelle: PRODUCT_TYPE_LABELS_PLURIEL[t] })),
];

/** Résumé lisible des attributs propres au type du produit. */
function resumeAttributs(p: Product): string {
  const valeurs = Object.values(p.attributs).filter(Boolean);
  return valeurs.length ? valeurs.join(' · ') : '—';
}

export function StockPage() {
  const [params, setParams] = useSearchParams();

  // `?q=` vient de la palette : le stock n'a pas de fiche produit, on y
  // arrive donc filtré sur le nom.
  const [recherche, setRecherche] = useState(params.get('q') ?? '');
  const [type, setType] = useState<Filtre>('TOUS');
  const [stockFaible, setStockFaible] = useState(params.get('stock_faible') === '1');
  const [page, setPage] = useState(1);
  const [tri, setTri] = useState<Tri>('date_ajout');
  const [sens, setSens] = useState<'asc' | 'desc'>('desc');

  const [edition, setEdition] = useState<{ ouvert: boolean; produit: Product | null }>({
    ouvert: false,
    produit: null,
  });
  const [reassort, setReassort] = useState<{ id: string; nom: string } | null>(null);
  const [aSupprimer, setASupprimer] = useState<Product | null>(null);

  const supprimer = useSupprimerProduit();
  const toast = useToast();

  const { data, isPending, error, refetch, isFetching } = useProduits({
    page,
    limit: 25,
    q: recherche || undefined,
    type: type === 'TOUS' ? undefined : type,
    stock_faible: stockFaible || undefined,
    tri,
    sens,
  });

  function trierPar(colonne: Tri) {
    if (colonne === tri) setSens((s) => (s === 'asc' ? 'desc' : 'asc'));
    else {
      setTri(colonne);
      setSens('asc');
    }
    setPage(1);
  }

  function basculerStockFaible(actif: boolean) {
    setStockFaible(actif);
    setPage(1);
    const suivants = new URLSearchParams(params);
    if (actif) suivants.set('stock_faible', '1');
    else suivants.delete('stock_faible');
    setParams(suivants, { replace: true });
  }

  async function confirmerSuppression() {
    if (!aSupprimer) return;
    try {
      await supprimer.mutateAsync(aSupprimer.id);
      toast.succes(`« ${aSupprimer.nom} » retiré du stock.`);
      setASupprimer(null);
    } catch (e) {
      toast.erreur(e instanceof Error ? e.message : 'Suppression impossible.');
    }
  }

  return (
    <>
      <PageHeader
        titre="Stock"
        description="Montures, verres et lentilles disponibles en magasin."
        actions={
          <Button
            variant="primary"
            icone={<Plus aria-hidden className="h-4 w-4" />}
            onClick={() => setEdition({ ouvert: true, produit: null })}
          >
            Nouveau produit
          </Button>
        }
      />

      <PageBody>
        <Card>
          <div className="flex flex-wrap items-center gap-3 px-4 pt-3">
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              />
              <Input
                value={recherche}
                onChange={(e) => {
                  setRecherche(e.target.value);
                  setPage(1);
                }}
                placeholder="Rechercher un produit…"
                className="pl-8"
                aria-label="Rechercher un produit"
              />
            </div>
            <Checkbox
              label={`Stock faible (≤ ${SEUIL_STOCK_FAIBLE})`}
              checked={stockFaible}
              onChange={(e) => basculerStockFaible(e.target.checked)}
            />
            {isFetching ? <span className="text-xs text-ink-muted">Actualisation…</span> : null}
          </div>

          <Tabs
            className="mt-3 px-4"
            onglets={ONGLETS}
            actif={type}
            onChange={(v) => {
              setType(v);
              setPage(1);
            }}
          />

          {isPending ? (
            <ChargementBloc />
          ) : error ? (
            <EtatErreur erreur={error} onReessayer={() => void refetch()} />
          ) : data.data.length === 0 ? (
            <EtatVide
              titre="Aucun produit"
              description={
                recherche || stockFaible || type !== 'TOUS'
                  ? 'Aucun produit ne correspond à ces critères.'
                  : 'Commencez par ajouter vos montures, verres et lentilles.'
              }
              icone={<Package aria-hidden className="h-6 w-6" />}
              action={
                <Button variant="primary" onClick={() => setEdition({ ouvert: true, produit: null })}>
                  Nouveau produit
                </Button>
              }
            />
          ) : (
            <>
              <Table>
                <THead>
                  <TR>
                    <THTri colonne="nom" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Produit
                    </THTri>
                    <TH>Type</TH>
                    <TH>Attributs</TH>
                    <THTri colonne="quantite" triActuel={tri} sens={sens} onTrier={trierPar} numerique>
                      Qté
                    </THTri>
                    <TH numerique>Prix d'achat</TH>
                    <THTri colonne="prix_vente" triActuel={tri} sens={sens} onTrier={trierPar} numerique>
                      Prix de vente
                    </THTri>
                    <THTri colonne="date_ajout" triActuel={tri} sens={sens} onTrier={trierPar}>
                      Ajouté le
                    </THTri>
                    <TH className="text-right">Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {data.data.map((p) => {
                    const faible = p.quantite <= SEUIL_STOCK_FAIBLE;
                    return (
                      <TR key={p.id} alerte={faible}>
                        <TD className="font-medium">{p.nom}</TD>
                        <TD>
                          <Badge ton="neutre">{PRODUCT_TYPE_LABELS_PLURIEL[p.type].slice(0, -1)}</Badge>
                        </TD>
                        <TD className="text-ink-muted">{tronquer(resumeAttributs(p), 32)}</TD>
                        <TD numerique>
                          <span
                            className={
                              p.quantite === 0
                                ? 'font-semibold text-danger'
                                : faible
                                  ? 'font-semibold text-mustard-600'
                                  : ''
                            }
                          >
                            {entier(p.quantite)}
                          </span>
                        </TD>
                        <TD numerique className="text-ink-muted">
                          {dh(p.prix_initial)}
                        </TD>
                        <TD numerique>{dh(p.prix_vente)}</TD>
                        <TD className="whitespace-nowrap text-ink-muted">{date(p.date_ajout)}</TD>
                        <TD className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Réapprovisionner ${p.nom}`}
                              title="Réapprovisionner"
                              onClick={() => setReassort({ id: p.id, nom: p.nom })}
                            >
                              <RotateCw aria-hidden className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Modifier ${p.nom}`}
                              title="Modifier"
                              onClick={() => setEdition({ ouvert: true, produit: p })}
                            >
                              <Pencil aria-hidden className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Supprimer ${p.nom}`}
                              title="Supprimer"
                              onClick={() => setASupprimer(p)}
                            >
                              <Trash2 aria-hidden className="h-4 w-4 text-danger" />
                            </Button>
                          </div>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
              <Pagination meta={data.meta} onPage={setPage} libelle="produits" />
            </>
          )}
        </Card>
      </PageBody>

      <ProduitDialog
        ouvert={edition.ouvert}
        produit={edition.produit}
        onFermer={() => setEdition({ ouvert: false, produit: null })}
      />

      <RestockDialog produit={reassort} onFermer={() => setReassort(null)} />

      <ConfirmDialog
        ouvert={Boolean(aSupprimer)}
        onFermer={() => setASupprimer(null)}
        onConfirmer={() => void confirmerSuppression()}
        titre="Retirer ce produit ?"
        libelleConfirmer="Retirer du stock"
        danger
        chargement={supprimer.isPending}
        message={
          <>
            <p>
              « {aSupprimer?.nom} » n'apparaîtra plus dans le stock. Les factures qui le citent
              restent intactes : le produit est archivé, jamais effacé.
            </p>
          </>
        }
      />
    </>
  );
}
